import * as d3 from "d3";
import { canNodeBeExpanded, getSizeForNode, panToNodeSmooth, getHeightForNode, getRoundCornerAmount } from "./graphUtility";

var transitionTime = 250;

let initStrokeWidth = 1.5;
let hoverStrokeWidth = 2.5;

const nodeTimers = new Map();

const customColors = [
    "#5C2228", // chocolate cosmos: Dark enough for white text
    "#111822", // rich black: Dark enough for white text
    "#397A84", // teal: Dark enough for white text
    "#D1975D", // sunset: Adjusted to a darker shade
    "#C8821E", // selective yellow: Adjusted to a darker shade
    "#B98456", // caramel: Marginal but should be okay for white text
    "#F33F1C", // scarlet: Bright, but should be okay for white text
    "#C9191E", // fire engine red: Bright, but should be okay for white text
    "#134C5A", // midnight green: Dark enough for white text
    "#C2B280"  // cosmic latte: Adjusted to a darker shade for better contrast
];

// Then, create a custom scaleOrdinal using your colors
const color = d3.scaleOrdinal(customColors);


function initSVG(width, height, ref, context) {

    const { svg, g, linkParent, nodeParent, labelParent } = context;

    d3.select(ref.current).selectAll("svg").remove();

    svg.current = d3.select(ref.current).
        append("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [-width / 2, -height / 2, width, height])
        .attr("style", "max-width: 100%; height: auto;");

    g.current = svg.current.append("g");

    linkParent.current = g.current.append("g")
        .attr("stroke", "#999")
        .attr("stroke-opacity", 0.5)
        .selectAll("line");

    nodeParent.current = g.current.append("g")
        .attr("stroke", "#fff")
        .attr("stroke-width", 1.5)
        .selectAll("circle");

    labelParent.current = g.current.append("g")
        .attr("class", "labels")
        .selectAll("text")
        .attr("font-size", "10px")
        .attr("text-anchor", "middle");

}

function updateData(nodes, links, onExpandNode, onSelectNode, context) {

    const { nodeParent, linkParent, labelParent, svg, zoom, currentNode, expandButtonParent } = context;
    linkParent.current = linkParent.current.data(links)
        .join("line")
        .attr("stroke-width", d => initStrokeWidth);

    nodeParent.current = nodeParent.current.data(nodes)
        .join("g") // Create a group for each node
        .attr("class", "node-group");

    nodeParent.current = nodeParent.current.data(nodes)
        .join("g") // Create a group for each node
        .attr("class", "node-group");


    nodeParent.current.append("rect")
        .attr("rx", d => getRoundCornerAmount(d.group))
        .attr("ry", d => getRoundCornerAmount(d.group))
        .attr("width", d => getSizeForNode(d.name.length, d.group))
        .attr("height", d => getHeightForNode(d.group))
        .attr("x", d => -getSizeForNode(d.name.length, d.group) / 2)
        .attr("y", d => -getHeightForNode(d.group) /2)
        .attr("fill", d => color(d.group))
        .on("click", handleClick)
        .style("cursor", "pointer")
        .on("mouseover", (event, d) => {
            event.stopPropagation();
            hoverOverNode(event, nodeParent, linkParent, labelParent);
        })
        .on("mouseout", (event, d) => {
            event.stopPropagation();
            hoverOutNode(event, nodeParent, linkParent, labelParent);
        });

    expandButtonParent.current = nodeParent.current.filter(d => canNodeBeExpanded(d.id, { nodes: nodes }))
        .each(function (d) {
            let node = d3.select(this);
            let expandButton = node.append("g"); // Group for expand button
            expandButton.append("circle")
                .attr("r", 25)
                .attr("cx", d => getSizeForNode(d.name.length, d.group) / 2 + 30)
                .attr("cy", 0)
                .style("fill", "var(--black-color)")
                .style("cursor", "pointer")
                .on("click", (event, d) => {
                    event.stopPropagation();
                    d3.select(this)
                        .select("circle")
                        .style("fill", "grey")
                        .style("opacity", 0.5)
                        .on("mouseover", null)
                        .on("mouseout", null)
                        .on("click", null);

                    updateColorsForExpandButtons([d.id], nodes, context);
                    let imageNode = d3.select(this).select("image"); // Assuming 'this' is the circle
                    rotateImageOnExpand(imageNode, d);
                    onExpandNode(d.id);
                    currentNode.current = d;
                })
                .on("mouseover", (event, d) => {
                    event.stopPropagation();
                    d3.select(event.target).style("fill", "var(--highlight-color)");
                })
                .on("mouseout", (event, d) => {
                    event.stopPropagation();
                    d3.select(event.target).style("fill", "var(--black-color)");
                });


            expandButton.append("image")
                .attr("xlink:href", "/plus.webp")
                .attr("width", 20)
                .attr("height", 20)
                .attr("x", d => getSizeForNode(d.name.length,  d.group) / 2 - 10 + 30)
                .attr("y", -10)
                .style("pointer-events", "none")
        });

    labelParent.current = labelParent.current.data(nodes)
        .join("text")
        .attr("text-anchor", "middle")
        .attr("dy", ".35em")
        .text(d => d.name)
        .style("pointer-events", "none")
        .attr("fill", "#fff")
        .attr("font-family", "Montserrat")
        .attr("font-size", "25px")


    function handleClick(event, d) {
        onSelectNode(d.id);
        panToNodeSmooth(d, svg, zoom);
    }
}


function rotateImageOnExpand(node, d) {
    let rotationDuration = 2000; // Duration of the rotation animation in milliseconds
    var timer = d3.timer(function (elapsed) {
        let angle = (elapsed / rotationDuration) * 360;
        node.attr("transform", "rotate(" + angle + " " + (getSizeForNode(d.name.length, d.group) / 2 + 30) + " " + 0 + ")");
        if (elapsed >= rotationDuration) return true;
    });

    nodeTimers.set(d.id, timer);
}

function stopRotation(node, d) {
    if (nodeTimers.has(d.id)) {
        nodeTimers.get(d.id).stop();
        nodeTimers.delete(d.id); 
    } 
    node.attr("transform", "rotate(0 " + (getSizeForNode(d.name.length, d.group) / 2 + 30) + " " + 0 + ")");
}


function initZoom(context) {
    const { g, svg, zoom, lastTransform } = context;

    zoom.current = d3.zoom()
        .scaleExtent([0.01, 1])
        .on('zoom', (event) => {
            g.current.attr('transform', event.transform); // Apply the transformation to the group element
            lastTransform.current = event.transform;
        });

    svg.current.call(zoom.current)
        .on("dblclick.zoom", null);

    svg.current.call(zoom.current.transform, lastTransform.current);
}


function hoverOverNode(event, nodeParent, linkParent, labelParent) {

    const node = event.target.__data__;
    d3.select(event.target)
        .transition()               // Start a transition
        .duration(transitionTime)              // Set the duration of the transition
        .attrTween("stroke", function (d) {
            var currentColor = d3.select(this).attr("stroke");
            var newColor = color(d.group);  // Assuming color is a scale function
            return d3.interpolateRgb(currentColor, newColor);
        })

    linkParent.current.filter(d => d.source.id === node.id || d.target.id === node.id)
        .transition()
        .duration(transitionTime)
        .attrTween("stroke", function (d) {
            var currentColor = d3.select(this).attr("stroke");
            var newColor = "black"
            return d3.interpolateRgb(currentColor, newColor);
        })
        .attr("stroke-width", d => hoverStrokeWidth);


    if (node.parent) {
        nodeParent.current.selectAll("circle")
            .filter(d => d.id === node.parent)
            .transition()
            .duration(transitionTime)
            .attrTween("stroke", function (d) {
                var currentColor = d3.select(this).attr("stroke");
                var newColor = color(d.group);
                newColor = d3.color(newColor);
                newColor.opacity = 0.5;
                return d3.interpolateRgb(currentColor, newColor);
            });
    }

    nodeParent.current.selectAll("circle") // Select all circles
        .filter(d => d.parent === node.id) // Filter to get only children of the current node
        .transition()
        .duration(transitionTime)
        .attrTween("stroke", function (d) {
            var currentColor = d3.select(this).attr("stroke");
            var newColor = color(d.group);  // Assuming color is a scale function
            newColor = d3.color(newColor);
            newColor.opacity = 0.5;
            return d3.interpolateRgb(currentColor, newColor);
        });

}

function hoverOutNode(event, nodeParent, linkParent, labelParent) {

    const node = d3.select(event.target).datum();
    d3.select(event.target)
        .transition()
        .duration(transitionTime)
        .attrTween("stroke", function (d) {
            var currentColor = d3.select(this).attr("stroke");
            var newColor = "transparent";
            return d3.interpolate(currentColor, newColor);
        });

    linkParent.current
        .transition()
        .duration(transitionTime)
        .attrTween("stroke", function (d) {
            var currentColor = d3.select(this).attr("stroke");
            var newColor = "#999";
            return d3.interpolate(currentColor, newColor);
        })
        .attr("stroke-width", d => initStrokeWidth);

    if (node.parent) {
        nodeParent.current.selectAll("circle")
            .filter(d => d.id === node.parent)
            .transition()
            .duration(transitionTime)
            .attrTween("stroke", function (d) {
                var currentColor = d3.select(this).attr("stroke");
                var newColor = "transparent";
                return d3.interpolateRgb(currentColor, newColor);
            });
    }

    nodeParent.current.selectAll("circle") // Select all circles
        .filter(d => d.parent === node.id) // Filter to get only children of the current node
        .transition()
        .duration(transitionTime)
        .attrTween("stroke", function (d) {
            var currentColor = d3.select(this).attr("stroke");
            var newColor = "transparent";
            return d3.interpolateRgb(currentColor, newColor);
        });
}

function addDragBehaviour(context) {

    const { nodeParent, simulation } = context;

    nodeParent.current.call(d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended));

    function dragstarted(event) {
        if (!event.active)
            simulation.current.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
    }

    function dragged(event) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
    }

    function dragended(event) {
        if (!event.active) simulation.current.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
    }
}

function updateColorsForExpandButtons(currentlyExpandingNodes, nodes, onExpandNode,context) {
    if (!context) { return; }
    const { nodeParent, expandButtonParent, currentNode } = context;

    if (!currentlyExpandingNodes) {
        return;
    }
    if (!nodeParent || !nodeParent.current) {
        return;
    }


    expandButtonParent.current.filter(d => canNodeBeExpanded(d.id, { nodes: nodes }))
        .each(function (d) {
            const isCurrentlyExpanding = currentlyExpandingNodes && currentlyExpandingNodes.includes(d.id);
            if (isCurrentlyExpanding && !nodeTimers.has(d.id)) {
                d3.select(this).select("circle")
                    .style("fill", "#678678")
                    .style("opacity", 0.5)
                    .on("mouseover", null)
                    .on("mouseout", null)
                    .on("click", null);

                var image = d3.select(this).select("image");
                rotateImageOnExpand(image, d);
            } else {
                d3.select(this).select("circle")
                    .style("fill", "var(--black-color)")
                    .style("opacity", 1)
                    .on("mouseover", (event, d) => {
                        event.stopPropagation();
                        d3.select(event.target).style("fill", "var(--highlight-color)");
                    })
                    .on("mouseout", (event, d) => {
                        event.stopPropagation();
                        d3.select(event.target).style("fill", "var(--black-color)");
                    })
                    .on("click", (event, d) => {
                        event.stopPropagation();

                        d3.select(this)
                        .select("circle")
                        .style("fill", "grey")
                        .style("opacity", 0.5)
                        .on("mouseover", null)
                        .on("mouseout", null)
                        .on("click", null);


                        updateColorsForExpandButtons([d.id], nodes, context);
                        let imageNode = d3.select(this).select("image"); 
                        rotateImageOnExpand(imageNode, d);
                        onExpandNode(d.id);
                        currentNode.current = d;
                    });
                
                    stopRotation(d3.select(this).select("image"), d);
            }
        });
}



export { initSVG, initZoom, updateData, addDragBehaviour, updateColorsForExpandButtons, hoverOverNode, hoverOutNode, transitionTime, customColors, color, initStrokeWidth, hoverStrokeWidth, panToNodeSmooth };


